import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiSend, FiMail, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { getSuggestedDonationAmount } from '../utils/costCalculator';
// CONTACT_CONFIG removed since all contact info is now hardcoded

const Section = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, var(--light-gray) 0%, white 100%);
  &#contact {
    scroll-margin-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.3rem;
  color: var(--medium-gray);
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
`;

const ContactTitle = styled.h3`
  font-size: 2rem;
  color: var(--primary-blue);
  margin-bottom: 24px;
`;

const ContactDescription = styled.p`
  font-size: 1.1rem;
  color: var(--medium-gray);
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ContactDetails = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 32px;
`;

const ContactDetail = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const ContactIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ContactText = styled.div`
  h4 {
    font-size: 1.1rem;
    color: var(--primary-blue);
    margin-bottom: 4px;
  }

  p {
    color: var(--medium-gray);
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const FormContainer = styled(motion.div)`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h3`
  font-size: 2rem;
  color: var(--primary-blue);
  margin-bottom: 8px;
`;

const FormSubtitle = styled.p`
  color: var(--medium-gray);
  margin-bottom: 32px;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: grid;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-orange);
  }

  &::placeholder {
    color: var(--medium-gray);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-orange);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #E2E8F0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-orange);
  }

  &::placeholder {
    color: var(--medium-gray);
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: var(--secondary-green);
  color: white;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  margin-top: 16px;
`;

const ErrorMessage = styled(motion.div)`
  background: #EF4444;
  color: white;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  margin-top: 16px;
`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    sponsorshipType: '',
    preferredDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      
      if (!GOOGLE_SCRIPT_URL) {
        throw new Error('Google Script URL not configured');
      }
      
      const submissionData = {
        ...formData,
        timestamp: new Date().toLocaleString(),
        source: 'Ahaar Sewa Website',
        submittedAt: new Date().toISOString()
      };
      
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors', // Required for Google Apps Script
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      // Since we use no-cors mode, we can't read the response
      // We'll assume success if no error is thrown
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          sponsorshipType: '',
          preferredDate: '',
          message: ''
        });
      }, 5000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
      setSubmitError('There was an error submitting your form. Please try again or email us directly at burritosewa@gmail.com');
    }
  };

  return (
    <Section id="contact">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Get Involved Today
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Ready to make a difference? Reach out to us to sponsor an event 
          or learn more about Ahaar Sewa's impact in the Bay Area community.
        </SectionSubtitle>

        <ContentGrid>
          <ContactInfo
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <ContactTitle>Let's Connect</ContactTitle>
            <ContactDescription>
              We're here to answer your questions, discuss sponsorship opportunities, 
              and help you find the perfect way to get involved with our mission.
            </ContactDescription>

            <ContactDetails>
              <ContactDetail>
                <ContactIcon>
                  <FiMail />
                </ContactIcon>
                <ContactText>
                  <h4>Email Us</h4>
                  <p>burritosewa@gmail.com</p>
                </ContactText>
              </ContactDetail>


            </ContactDetails>

            <ContactDescription>
              <strong>Event Schedule:</strong><br />
              Burrito-making events every 2 weeks<br />
              Custom events for sponsors year-round<br />
              <strong>Response Time:</strong> Within 24 hours
            </ContactDescription>
          </ContactInfo>

          <FormContainer
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <FormTitle>Sponsor an Event</FormTitle>
            <FormSubtitle>
              Fill out the form below and we'll get back to you within 24 hours.
            </FormSubtitle>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="organization">Organization (Optional)</Label>
                <Input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Company or organization name"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="sponsorshipType">Sponsorship Interest</Label>
                <Select
                  id="sponsorshipType"
                  name="sponsorshipType"
                  value={formData.sponsorshipType}
                  onChange={handleChange}
                >
                  <option value="">Select sponsorship level</option>
                  <option value="custom-donation">💝 Custom Donation (Any Amount)</option>
                  <option value="supporter">🤝 Community Supporter (${getSuggestedDonationAmount(50)})</option>
                  <option value="contributor">💫 Impact Contributor (${getSuggestedDonationAmount(100)})</option>
                  <option value="community-champion">🍽️ Community Champion - Full Event (${getSuggestedDonationAmount(210)})</option>
                  <option value="impact-hero">🏆 Impact Hero - Large Event (${getSuggestedDonationAmount(400)}+)</option>
                  <option value="custom">Custom Amount (Above Listed Tiers)</option>

                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="preferredDate">Preferred Event Date</Label>
                <Input
                  type="text"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  placeholder="e.g., Next month, specific date, flexible"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your interest in sponsoring, any special occasion you'd like to honor, questions you have, or anything else you'd like us to know..."
                />
              </FormGroup>

              <SubmitButton
                type="submit"
                disabled={isSubmitting || isSubmitted}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? (
                  'Sending...'
                ) : isSubmitted ? (
                  <>
                    <FiCheckCircle />
                    Message Sent!
                  </>
                ) : (
                  <>
                    <FiSend />
                    Send Message
                  </>
                )}
              </SubmitButton>

              {isSubmitted && (
                <SuccessMessage
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <FiCheckCircle />
                  Thank you! We'll be in touch within 24 hours.
                </SuccessMessage>
              )}

              {submitError && (
                <ErrorMessage
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <FiAlertCircle />
                  {submitError}
                </ErrorMessage>
              )}
            </Form>
          </FormContainer>
        </ContentGrid>
      </Container>
    </Section>
  );
};

export default ContactForm; 